import { services } from '@config/services';
import { catalogApi } from './base';
import type { EffectiveVariantResponse, Product, ProductVariant } from './types';

/**
 * "Effective" catalog endpoints — the merchant's resolved view of available
 * products/variants (master + overrides). `getEffectiveVariant` is preserved
 * verbatim from the original stub (URL, body, tag) for the orders consumer;
 * the rest back the Add Products wizard's fetch-all flows.
 */
export const effectiveApi = catalogApi.injectEndpoints({
  endpoints: (builder) => ({
    /** POST /catalog/v1/merchantCatalog/effective/variants
     *  body: { merchantId, skus }. Used by orders OrderDetails for DPI math. */
    getEffectiveVariant: builder.query<
      EffectiveVariantResponse,
      { merchantId: string; skus: string[] }
    >({
      query: (body) => ({
        url: `${services.catalog}/v1/merchantCatalog/effective/variants`,
        method: 'POST',
        body,
      }),
      providesTags: (_r, _e, { merchantId, skus }) => [
        { type: 'EffectiveVariant', id: `${merchantId}:${skus.join(',')}` },
      ],
    }),

    /** GET /catalog/v1/merchantCatalog/effective/products — one page; the
     *  wizard's fetch-all hook loops this with chunked concurrency. */
    getEffectiveProducts: builder.query<
      { products?: Product[]; total?: number; totalProducts?: number },
      { merchantId: string; page?: number; pageSize?: number }
    >({
      query: ({ merchantId, page = 1, pageSize = 250 }) => ({
        url: `${services.catalog}/v1/merchantCatalog/effective/products?merchantId=${encodeURIComponent(merchantId)}&page=${page}&pageSize=${pageSize}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Product', id: 'EFFECTIVE_LIST' }],
    }),

    /** GET /catalog/v1/merchantCatalog/effective/products/{productId}/variants —
     *  one page; callers needing every variant must loop it via `fetchAllPages`. */
    getEffectiveVariantsByProduct: builder.query<
      { variants?: ProductVariant[]; totalVariants?: number },
      { merchantId: string; productId: string; page?: number; pageSize?: number }
    >({
      query: ({ merchantId, productId, page = 1, pageSize = 250 }) => ({
        url: `${services.catalog}/v1/merchantCatalog/effective/products/${encodeURIComponent(productId)}/variants?merchantId=${encodeURIComponent(merchantId)}&page=${page}&pageSize=${pageSize}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { productId }) => [{ type: 'ProductVariant', id: productId }],
    }),

    /** POST /catalog/v1/merchantCatalog/effective/variants/separate — resolve
     *  variant info by merchantSku and/or sku during upload. */
    getEffectiveVariantsSeparate: builder.mutation<
      EffectiveVariantResponse,
      { merchantId: string; merchantSkus?: string[]; skus?: string[] }
    >({
      query: ({ merchantId, merchantSkus = [], skus = [] }) => ({
        url: `${services.catalog}/v1/merchantCatalog/effective/variants/separate`,
        method: 'POST',
        body: { merchantId, merchantSkus, skus },
      }),
    }),
  }),
});

export const {
  useGetEffectiveVariantQuery,
  useGetEffectiveProductsQuery,
  useLazyGetEffectiveProductsQuery,
  useGetEffectiveVariantsByProductQuery,
  useGetEffectiveVariantsSeparateMutation,
} = effectiveApi;
